/**
 * ZoneClient — WebSocket connection to one zone Durable Object.
 *
 * Owns:
 *   - Connection lifecycle + auto-reconnect
 *   - Outbound throttling to CLIENT_SEND_HZ
 *   - Inbound message dispatch via shared/protocol.ts
 *   - Remote-player snapshot store with interpolation buffer
 *   - Chat message broadcast/receive
 *
 * Zone handoff is owned by ZoneClient via `connectTo(zoneId)` — the host
 * code calls it whenever the player crosses a boundary.
 */

import {
  CLIENT_SEND_HZ,
  ServerMsgType,
  ChatChannels,
  encodeHello,
  encodePosUpdate,
  encodeChat,
  decodeServerMessage,
  type PlayerRecord,
  type ChatChannel,
  AnimState
} from '@shared/protocol';
import { useUI } from '@/state/store';

export interface RemotePlayer {
  id: number;
  name: string;
  hatColor: number;
  // Latest server position
  x: number;
  y: number;
  z: number;
  yaw: number;
  anim: AnimState;
  // Interpolation: previous + target with timestamps
  prev: { x: number; y: number; z: number; yaw: number; t: number };
  target: { x: number; y: number; z: number; yaw: number; t: number };
}

export type ZoneEvent =
  | { type: 'welcome'; yourId: number; serverTime: number; zoneName: string }
  | { type: 'snapshot'; players: PlayerRecord[] }
  | { type: 'left'; id: number }
  | { type: 'chat'; fromId: number; channel: ChatChannel; text: string }
  | { type: 'connected' }
  | { type: 'disconnected' };

const SEND_INTERVAL_MS = Math.round(1000 / CLIENT_SEND_HZ);
const RECONNECT_DELAY_MS = 2000;

export class ZoneClient {
  private ws: WebSocket | null = null;
  private wsUrlBase: string;
  private currentZone: string | null = null;
  private listeners: ((e: ZoneEvent) => void)[] = [];

  yourId = 0;
  remotePlayers = new Map<number, RemotePlayer>();
  connected = false;

  private lastSendTs = 0;
  private pendingHello: { name: string; hatColor: number } | null = null;
  private reconnectTimer: number | null = null;

  constructor(wsUrlBase: string) {
    // Strip trailing slash so we can append /ws/...
    this.wsUrlBase = wsUrlBase.replace(/\/$/, '');
  }

  on(fn: (e: ZoneEvent) => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }

  setIdentity(name: string, hatColor: number): void {
    this.pendingHello = { name, hatColor };
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(encodeHello(name, hatColor));
    }
  }

  /** Connect (or hand off) to a zone. Disconnects from any previous zone. */
  connectTo(zoneId: string): void {
    if (this.currentZone === zoneId && this.connected) return;
    this.currentZone = zoneId;
    this.disconnect();
    const url = `${this.wsUrlBase}/ws/${zoneId}`;
    try {
      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';
      ws.onopen = () => {
        this.connected = true;
        this.emit({ type: 'connected' });
        useUI.getState().setConnected(true);
        useUI.getState().setCurrentZone(zoneId);
        if (this.pendingHello) {
          ws.send(encodeHello(this.pendingHello.name, this.pendingHello.hatColor));
        }
      };
      ws.onmessage = (ev) => this.handleMessage(ev.data as ArrayBuffer);
      ws.onclose = () => {
        this.connected = false;
        this.remotePlayers.clear();
        useUI.getState().setConnected(false);
        this.emit({ type: 'disconnected' });
        this.scheduleReconnect();
      };
      ws.onerror = () => {
        // onclose will follow
      };
      this.ws = ws;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[net] failed to connect', err);
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }
    this.connected = false;
    this.remotePlayers.clear();
  }

  private scheduleReconnect(): void {
    if (!this.currentZone) return;
    if (this.reconnectTimer !== null) return;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.currentZone) this.connectTo(this.currentZone);
    }, RECONNECT_DELAY_MS);
  }

  /** Send our position to the server, throttled to CLIENT_SEND_HZ. */
  sendPosition(x: number, y: number, z: number, yaw: number, anim: AnimState): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const now = performance.now();
    if (now - this.lastSendTs < SEND_INTERVAL_MS) return;
    this.lastSendTs = now;
    this.ws.send(encodePosUpdate(x, y, z, yaw, anim));
  }

  sendChat(channel: ChatChannel, text: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (!text.trim()) return;
    this.ws.send(encodeChat(channel, text));
  }

  private handleMessage(data: ArrayBuffer): void {
    const msg = decodeServerMessage(data);
    switch (msg.type) {
      case ServerMsgType.Welcome: {
        this.yourId = msg.yourId;
        this.emit({ type: 'welcome', yourId: msg.yourId, serverTime: msg.serverTime, zoneName: msg.zoneName });
        break;
      }
      case ServerMsgType.Snapshot: {
        const seen = new Set<number>();
        const t = performance.now();
        for (const p of msg.players) {
          seen.add(p.id);
          if (p.id === this.yourId) continue;
          let rp = this.remotePlayers.get(p.id);
          if (!rp) {
            rp = {
              id: p.id,
              name: p.name,
              hatColor: p.hatColor,
              x: p.x, y: p.y, z: p.z, yaw: p.yaw, anim: p.anim,
              prev: { x: p.x, y: p.y, z: p.z, yaw: p.yaw, t },
              target: { x: p.x, y: p.y, z: p.z, yaw: p.yaw, t }
            };
            this.remotePlayers.set(p.id, rp);
          } else {
            rp.prev = { x: rp.target.x, y: rp.target.y, z: rp.target.z, yaw: rp.target.yaw, t: rp.target.t };
            rp.target = { x: p.x, y: p.y, z: p.z, yaw: p.yaw, t };
            rp.x = p.x; rp.y = p.y; rp.z = p.z; rp.yaw = p.yaw;
            rp.anim = p.anim;
            rp.name = p.name;
            rp.hatColor = p.hatColor;
          }
        }
        // Drop players who weren't in the snapshot.
        for (const id of Array.from(this.remotePlayers.keys())) {
          if (!seen.has(id) && id !== this.yourId) {
            this.remotePlayers.delete(id);
          }
        }
        useUI.getState().setPlayerCount(msg.players.length);
        this.emit({ type: 'snapshot', players: msg.players });
        break;
      }
      case ServerMsgType.PlayerLeft: {
        this.remotePlayers.delete(msg.id);
        this.emit({ type: 'left', id: msg.id });
        break;
      }
      case ServerMsgType.Chat: {
        this.emit({ type: 'chat', fromId: msg.fromId, channel: msg.channel, text: msg.text });
        // Push to UI store
        const fromPlayer = this.remotePlayers.get(msg.fromId);
        const fromName =
          msg.fromId === this.yourId
            ? useUI.getState().identity?.displayName ?? 'You'
            : fromPlayer?.name ?? `Player${msg.fromId}`;
        useUI.getState().pushChat({
          id: 0,
          fromName,
          channel: msg.channel,
          text: msg.text,
          ts: Date.now()
        });
        break;
      }
    }
  }

  private emit(e: ZoneEvent): void {
    for (const l of this.listeners) l(e);
  }
}

export { ChatChannels };
